import { useEffect, useState, useCallback, useRef } from 'react';
import ApiDatabase from '../../../../../../server';

// Types de documents à récupérer
const DOCUMENT_TYPES_TO_FETCH = [
  { type: 'identity', label: "Pièce d'identité" },
  { type: 'selfie', label: 'Selfie' },
  { type: 'visa', label: 'Visa / Titre de séjour' },
  { type: 'card_vitality', label: 'Carte Vitale' },
  { type: 'proof_of_address', label: 'Justificatif de domicile' },
  { type: 'accommodation_of_address', label: "Attestation d'hébergement" },
  { type: 'hostIdentity', label: "Pièce d'identité de l'hôte" },
];

// Cache global pour les documents (persiste entre les montages/démontages)
// Structure: { [adminUserId]: { documents: [], timestamp: Date.now(), complete: boolean } }
const documentsCache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Délai entre les requêtes pour éviter les conflits d'AbortController
const REQUEST_DELAY = 100; // 100ms

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Parse les URLs de documents retournés par l'API
 */
const parseDocUrls = (data) => {
  const urls = Array.isArray(data?.urls) ? data.urls : [];
  return urls.filter(Boolean).map((item) => {
    const href = item.url || item.uri || '';
    let originalFileName = '';
    try {
      const u = new URL(href);
      originalFileName = decodeURIComponent(
        (u.pathname.split('/').pop() || '').trim()
      );
    } catch (e) {
      const clean = href.split('?')[0].split('#')[0];
      originalFileName = decodeURIComponent(
        (clean.split('/').pop() || '').trim()
      );
    }
    return {
      uri: href,
      fileName: originalFileName || 'document',
      fileType: item.type || 'application/pdf',
    };
  });
};

/**
 * Vérifie si le cache est valide et complet pour un utilisateur
 */
const isCacheValid = (adminUserId) => {
  const cached = documentsCache[adminUserId];
  if (!cached) return false;
  // Le cache doit être complet ET non expiré
  if (!cached.complete) return false;
  return Date.now() - cached.timestamp < CACHE_DURATION;
};

/**
 * Hook pour récupérer tous les documents d'un utilisateur
 * Les documents sont mis en cache pour éviter les rechargements inutiles.
 * 
 * @param {object} params
 * @param {string} params.token - Token d'authentification
 * @param {string} params.adminUserId - ID de l'utilisateur
 * @param {string} params.currentType - Type de document actuellement affiché (exclu de la liste)
 * @param {boolean} params.enabled - Activer le chargement (doit être false pendant le chargement du doc principal)
 * @returns {object} { allDocuments, isLoading }
 */
const useAllUserDocuments = ({ 
  token, 
  adminUserId, 
  currentType = null,
  enabled = true 
}) => {
  const [allDocuments, setAllDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useRef(true);

  const fetchAllDocuments = useCallback(async () => {
    if (!adminUserId || !token) {
      setAllDocuments([]);
      setIsLoading(false);
      return;
    }

    // Vérifier le cache (doit être complet et non expiré)
    if (isCacheValid(adminUserId)) {
      setAllDocuments(documentsCache[adminUserId].documents);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const documents = [];

    // Récupérer tous les types (pas de filtre par currentType pour le cache)
    for (let i = 0; i < DOCUMENT_TYPES_TO_FETCH.length; i++) {
      const { type, label } = DOCUMENT_TYPES_TO_FETCH[i];
      
      if (!isMountedRef.current) break;

      // Ajouter un délai entre les requêtes pour éviter les conflits
      if (i > 0) {
        await delay(REQUEST_DELAY);
      }

      try {
        const result = await new Promise((resolve) => {
          ApiDatabase.postPresignFiles(
            {
              token,
              idUser: adminUserId,
              type,
              fileName: '',
            },
            (data) => {
              const docs = parseDocUrls(data);
              if (docs.length > 0) {
                resolve({
                  type,
                  label,
                  docs,
                });
              } else {
                resolve(null);
              }
            },
            (err) => {
              if (err?.name !== 'CanceledError') {
                console.log(`Error fetching ${type}:`, err);
              }
              resolve(null);
            }
          );
        });

        if (result && isMountedRef.current) {
          documents.push(result);
          // Mettre à jour progressivement
          setAllDocuments([...documents]);
        }
      } catch (err) {
        console.log(`Error fetching ${type}:`, err);
      }
    }

    // Sauvegarder dans le cache seulement si complet
    if (isMountedRef.current) {
      documentsCache[adminUserId] = {
        documents,
        timestamp: Date.now(),
        complete: true,
      };
      setAllDocuments(documents);
      setIsLoading(false);
    }
  }, [token, adminUserId]);

  // Filtrer les documents pour exclure le type actuel
  const filteredDocuments = allDocuments.filter(d => d.type !== currentType);

  // Invalider le cache et refetch
  const invalidateAndRefetch = useCallback(() => {
    if (adminUserId) {
      delete documentsCache[adminUserId];
    }
    fetchAllDocuments();
  }, [adminUserId, fetchAllDocuments]);

  useEffect(() => {
    isMountedRef.current = true;
    
    if (enabled) {
      fetchAllDocuments();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [enabled, fetchAllDocuments]);

  return { 
    allDocuments: filteredDocuments, 
    isLoading, 
    refetch: fetchAllDocuments,
    invalidateAndRefetch,
  };
};

export default useAllUserDocuments;
